'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';
import { GraduationCap, Sparkles, X, Trash2 } from 'lucide-react';

import { useAIAssistant } from '@/contexts/AIAssistantContext';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { detectModuleContext } from '@/lib/diagnostics/probes';
import { getModuleConfig, type AssistantModuleConfig } from '@/lib/ai/assistantModuleConfigs';
import type { DemoSessionInfo } from '@/lib/demo/types';
import AIAssistantChat, { type AIMessage, type QuickAction, type ExamplePrompt } from '@/components/shared/AIAssistantChat';

// ============================================================================
// CONSTANTS
// ============================================================================

const BACKDROP_Z_CLASS = 'z-[140]';
const PANEL_Z_CLASS = 'z-[141]';
const CONFIRM_Z_CLASS = 'z-[142]';

// Fallback "dashboard" config for when no module is detected
const DASHBOARD_CONFIG: AssistantModuleConfig = {
  moduleKey: 'dashboard',
  moduleContext: 'dashboard',
  title: 'Total Assistant',
  subtitle: 'Powered by Total Access',
  welcomeTitle: 'Total Assistant',
  welcomeMessage: 'Your AI-powered assistant for navigating TotalAccess. Ask me anything about the platform, your tasks, or get help with features.',
  inputPlaceholder: 'Ask me anything...',
  primaryColor: '#3b82f6',
  quickActions: [],
  examplePrompts: [],
  showProviderToggle: false,
  showHistory: true,
  enableSemanticContext: true,
  enableSemanticIntake: false,
  enableModuleAssistance: true,
};

// ============================================================================
// TYPES
// ============================================================================

interface UnifiedAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  demoSession?: DemoSessionInfo | null;
}

interface ModuleAiAssistance {
  module_name?: string;
  module_tagline?: string;
  description?: string;
  icon?: string;
  welcome_template?: string;
  quick_suggestions?: Array<string | { text?: string; label?: string }>;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function UnifiedAssistantModal({ isOpen, onClose, demoSession = null }: UnifiedAssistantModalProps) {
  const pathname = usePathname();
  const { currentTheme } = useTheme();
  const authHook = useAuth();
  const user = authHook?.user;
  const panelRef = useRef<HTMLDivElement>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [moduleAiAssistance, setModuleAiAssistance] = useState<ModuleAiAssistance | null>(null);

  // --- Module detection from URL ---
  const detectedModule = useMemo(() => {
    if (!pathname) return null;
    const pathParts = pathname.split('/').filter(Boolean);
    const isInModule = pathParts.length >= 3 && pathParts[0] === 'client-zone' && pathParts[1] === 'modules';
    return isInModule ? pathParts[2] : null;
  }, [pathname]);

  // Also use the more sophisticated detector as fallback
  const detectedContext = useMemo(() => {
    if (detectedModule) return { module: detectedModule, source: 'currentPath' as const };
    return detectModuleContext();
  }, [detectedModule]);

  const moduleKey = detectedContext.module || 'dashboard';
  const isOperationsTrainingDemo = Boolean(demoSession?.is_demo_mode && moduleKey === 'total-operations');

  // --- Resolve config from registry or fallback to dashboard ---
  const baseConfig = useMemo(() => {
    return getModuleConfig(moduleKey) || DASHBOARD_CONFIG;
  }, [moduleKey]);

  // --- Fetch module AI assistance data when on a module page ---
  useEffect(() => {
    if (!isOpen || !moduleKey || moduleKey === 'dashboard') return;

    let cancelled = false;
    (async () => {
      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) return;

        const response = await fetch(`/api/v1/modules/ai-assistance?module_key=${moduleKey}`, {
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok && !cancelled) {
          const data = await response.json();
          if (data.module) {
            setModuleAiAssistance(data.module);
          }
        }
      } catch (error) {
        console.error('[UnifiedAssistantModal] Error fetching module AI assistance:', error);
      }
    })();

    return () => {
      cancelled = true;
      setModuleAiAssistance(null);
    };
  }, [isOpen, moduleKey]);

  // --- Build effective config (merge registry config with fetched module data) ---
  const effectiveConfig = useMemo<AssistantModuleConfig>(() => {
    if (!moduleAiAssistance) return baseConfig;

    const moduleName = moduleAiAssistance.module_name || baseConfig.title;
    const tagline = moduleAiAssistance.module_tagline || moduleAiAssistance.description || baseConfig.welcomeMessage;
    const icon = moduleAiAssistance.icon || '📦';

    let welcomeMessage = `👋 Hi ${user?.first_name || 'there'}! I'm your Total Assistant.\n\n${icon} ${moduleName}\n${tagline}\n\nWhat would you like help with today?`;

    if (moduleAiAssistance.welcome_template) {
      welcomeMessage = moduleAiAssistance.welcome_template
        .replace('{user_name}', user?.first_name || 'there')
        .replace('{module_name}', moduleName)
        .replace('{module_icon}', icon);
    }

    const suggestions: ExamplePrompt[] = [];
    if (moduleAiAssistance.quick_suggestions && Array.isArray(moduleAiAssistance.quick_suggestions)) {
      for (const suggestion of moduleAiAssistance.quick_suggestions) {
        const text = typeof suggestion === 'string' ? suggestion : (suggestion.text || suggestion.label || '');
        if (text) {
          suggestions.push({ icon: Sparkles, text });
        }
      }
    }

    return {
      ...baseConfig,
      title: moduleName,
      welcomeTitle: moduleName,
      welcomeMessage,
      examplePrompts: suggestions.length > 0 ? suggestions : baseConfig.examplePrompts,
    };
  }, [baseConfig, moduleAiAssistance, user?.first_name]);

  // --- Override welcome for demo/training mode ---
  const finalConfig = useMemo<AssistantModuleConfig>(() => {
    if (!isOperationsTrainingDemo || !demoSession) return effectiveConfig;

    const trainingLines: string[] = [
      `👋 Hi ${user?.first_name || 'there'}! Welcome to ${demoSession.display_name}.`,
      '',
      `You're in guided Total Operations training mode, and I'll keep the advice aligned to your live ${demoSession.role_label.toLowerCase()} access from ${demoSession.source_client_name}.`,
    ];

    if (demoSession.solution_keys.length > 0) {
      trainingLines.push('', `Active solution scope: ${demoSession.solution_keys.map((key) => key.replace(/[-_]+/g, ' ')).join(', ')}.`);
    }

    if (demoSession.training_modules.length > 0) {
      trainingLines.push('', 'Suggested practice areas:');
      demoSession.training_modules.slice(0, 2).forEach((trainingModule) => {
        trainingModule.focus_areas.slice(0, 2).forEach((focusArea) => {
          trainingLines.push(`• ${focusArea.name}`);
        });
      });
    }

    trainingLines.push('', 'What would you like to practise first?');

    const demoPrompts: ExamplePrompt[] = demoSession.assistant_prompts.slice(0, 6).map((prompt) => ({
      icon: Sparkles,
      text: prompt,
    }));

    return {
      ...effectiveConfig,
      welcomeMessage: trainingLines.join('\n'),
      examplePrompts: demoPrompts.length > 0 ? demoPrompts : effectiveConfig.examplePrompts,
    };
  }, [effectiveConfig, isOperationsTrainingDemo, demoSession, user?.first_name]);

  // --- useAIAssistant hook (core integration) ---
  const ai = useAIAssistant({
    moduleKey: finalConfig.moduleKey,
    moduleContext: finalConfig.moduleContext,
    enableSemanticContext: finalConfig.enableSemanticContext,
    enableSemanticIntake: finalConfig.enableSemanticIntake,
    enableModuleAssistance: finalConfig.enableModuleAssistance,
  });

  // --- ESC key handler ---
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // --- Prevent body scroll when open ---
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // --- Handlers ---
  const handleSendMessage = useCallback(async (message: string, files?: globalThis.File[], options?: { provider?: string; model?: string }) => {
    await ai.sendMessage(message, files, options);
  }, [ai]);

  const handleNewChat = useCallback(() => {
    ai.newChat();
    setShowClearConfirm(false);
  }, [ai]);

  const handleFeedback = useCallback((messageId: string, feedback: 'positive' | 'negative') => {
    ai.feedback(messageId, feedback);
  }, [ai]);

  const handleApplyAction = useCallback((action: Parameters<typeof ai.applyAction>[0]) => {
    ai.applyAction(action);
  }, [ai]);

  const handleLoadSession = useCallback(async (sessionId: string, sessionDate: string) => {
    await ai.loadSession(sessionId, sessionDate);
  }, [ai]);

  // --- Convert suggestedPrompts to example prompts if none from module data ---
  const effectiveExamplePrompts = useMemo<ExamplePrompt[]>(() => {
    if (finalConfig.examplePrompts.length > 0) return finalConfig.examplePrompts;
    if (ai.suggestedPrompts && ai.suggestedPrompts.length > 0) {
      return ai.suggestedPrompts.slice(0, 6).map((prompt) => ({ icon: Sparkles, text: prompt }));
    }
    // Default dashboard prompts
    return [
      { icon: Sparkles, text: 'Give me a platform tour' },
      { icon: Sparkles, text: 'Show me my tasks for today' },
      { icon: Sparkles, text: 'What modules do I have access to?' },
      { icon: Sparkles, text: 'Help me get started' },
    ];
  }, [finalConfig.examplePrompts, ai.suggestedPrompts]);

  // --- Quick actions ---
  const quickActions = useMemo<QuickAction[]>(() => {
    return finalConfig.quickActions;
  }, [finalConfig.quickActions]);

  if (!isOpen) return null;

  const isDark = currentTheme.mode === 'dark';

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm ${BACKDROP_Z_CLASS} transition-all duration-300`}
        onClick={onClose}
      />

      {/* Slide-in Panel */}
      <div
        ref={panelRef}
        className={`fixed right-0 w-full max-w-2xl shadow-2xl ${PANEL_Z_CLASS} flex flex-col animate-slide-in-right ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}
        style={{
          top: 'var(--safe-area-inset-top)',
          bottom: 'var(--safe-area-inset-bottom)',
          height: 'calc(100% - var(--safe-area-inset-top) - var(--safe-area-inset-bottom))',
        }}
      >
        {/* Custom header with close + clear chat */}
        <div className="relative p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg flex-shrink-0">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold truncate text-gray-900 dark:text-white">
                {finalConfig.title}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {finalConfig.subtitle}
              </p>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => {
                  onClose();
                  window.dispatchEvent(new CustomEvent('ai-coach-open'));
                }}
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-2 text-xs font-semibold text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200"
                aria-label="Switch to AI Training Coach"
                title="Switch to AI Training Coach"
              >
                <GraduationCap className="w-4 h-4" />
                Coach
              </button>
              {ai.messages.length > 0 && (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
                  aria-label="Clear chat"
                  title="Clear conversation"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* AIAssistantChat fills the remaining space */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <AIAssistantChat
            title={finalConfig.title}
            subtitle={finalConfig.subtitle}
            welcomeTitle={finalConfig.welcomeTitle}
            welcomeMessage={finalConfig.welcomeMessage}
            inputPlaceholder={finalConfig.inputPlaceholder}
            moduleContext={finalConfig.moduleContext}
            moduleKey={finalConfig.moduleKey}
            userName={user?.first_name}
            messages={ai.messages as AIMessage[]}
            isGenerating={ai.isGenerating}
            creditsRemaining={ai.credits?.remaining}
            creditsTotal={ai.credits?.total}
            creditsResetDate={ai.credits?.resetDate}
            showCredits
            quickActions={quickActions}
            examplePrompts={effectiveExamplePrompts}
            showQuickActions
            onSendMessage={handleSendMessage}
            onNewChat={handleNewChat}
            onFeedback={handleFeedback}
            onApplyAction={handleApplyAction}
            onLoadSession={handleLoadSession}
            showProviderToggle={finalConfig.showProviderToggle}
            showHistory={finalConfig.showHistory}
            primaryColor={finalConfig.primaryColor}
            useUnifiedTheming
            closable={false}
            minimizable={false}
            hideHeader
            className="h-full"
            showDisclaimer
          />
        </div>
      </div>

      {/* Clear Chat Confirmation Dialog */}
      {showClearConfirm && (
        <div
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm ${CONFIRM_Z_CLASS} flex items-center justify-center p-4`}
          onClick={() => setShowClearConfirm(false)}
        >
          <div
            className={`max-w-md w-full rounded-2xl shadow-2xl p-6 ${isDark ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-red-900/30' : 'bg-red-100'}`}>
                <Trash2 className={`w-6 h-6 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  Clear Conversation?
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  This action cannot be undone
                </p>
              </div>
            </div>
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Are you sure you want to clear your conversation history? All messages will be deleted and you&apos;ll start fresh.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleNewChat}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors bg-red-600 hover:bg-red-700 text-white"
              >
                Clear Chat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
}
